import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

interface DocumentDetail {
  id: string;
  title: string;
  body: string | null;
  externalUrl: string | null;
  isExternal: boolean;
  authorName: string;
  createdAt: string;
  updatedAt: string;
  module: {
    id: string;
    name: string;
    category: {
      id: string;
      name: string;
    };
  };
}

interface ContentViewerProps {
  documentId: string | null;
  onEdit: (doc: DocumentDetail) => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '2rem',
    height: '100%',
    overflowY: 'auto',
  },
  welcome: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#666',
    textAlign: 'center',
    padding: '2rem',
  },
  welcomeIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  welcomeTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1a6b3c',
    marginBottom: '0.5rem',
  },
  welcomeText: {
    fontSize: '0.9rem',
    lineHeight: 1.6,
    maxWidth: '400px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #e0e0e0',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: '0 0 0.35rem 0',
  },
  meta: {
    fontSize: '0.8rem',
    color: '#888',
  },
  editBtn: {
    padding: '0.4rem 0.8rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  body: {
    whiteSpace: 'pre-wrap',
    lineHeight: 1.7,
    fontSize: '0.9rem',
    color: '#333',
    wordBreak: 'break-word',
  },
  iframe: {
    width: '100%',
    height: 'calc(100% - 100px)',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#888',
  },
  error: {
    padding: '2rem',
    textAlign: 'center',
    color: '#d32f2f',
  },
  breadcrumb: {
    fontSize: '0.75rem',
    color: '#999',
    marginBottom: '0.25rem',
  },
};

export function ContentViewer({ documentId, onEdit }: ContentViewerProps) {
  const [doc, setDoc] = useState<DocumentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId) {
      setDoc(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiClient<DocumentDetail>(`/documents/${documentId}`)
      .then((data) => {
        if (!cancelled) {
          setDoc(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load document');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [documentId]);

  if (!documentId) {
    return (
      <div style={styles.welcome}>
        <div style={styles.welcomeIcon}>📚</div>
        <div style={styles.welcomeTitle}>Document Library</div>
        <p style={styles.welcomeText}>
          Select a document from the tree on the left to view its content.
          Use the search bar to find documents by title, or browse by category and module.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div style={styles.loading}>Loading document...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!doc) {
    return <div style={styles.error}>Document not found</div>;
  }

  const updatedDate = new Date(doc.updatedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.breadcrumb}>
            {doc.module.category.name} / {doc.module.name}
          </div>
          <h2 style={styles.title}>{doc.title}</h2>
          <div style={styles.meta}>
            By {doc.authorName} · Updated {updatedDate}
          </div>
        </div>
        <button style={styles.editBtn} onClick={() => onEdit(doc)}>
          ✏️ Edit
        </button>
      </div>

      {doc.isExternal && doc.externalUrl ? (
        <iframe
          src={doc.externalUrl}
          style={styles.iframe}
          title={doc.title}
          sandbox="allow-same-origin allow-scripts"
        />
      ) : (
        <div style={styles.body}>{doc.body || ''}</div>
      )}
    </div>
  );
}

export type { DocumentDetail };
